import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { AuthContext } from './AuthContext';
import { http, registerAuthHandlers, setAdminAccessToken } from '@/lib/http/apiFetcher';
import type { AuthContextValue, AuthLoginError, AuthenticatedUser, Role } from './types';

interface LoginApiResponse {
  accessToken: string;
  accessExpiresAt: string;
  user: { id: string; email: string; fullName: string; role: Role };
  tokenType: string;
}

/**
 * Owns the admin session in memory:
 *
 * - `login` exchanges email+password for an access JWT and identity. The
 *   refresh cookie travels as httpOnly so we only ever see the access token.
 * - `refresh` quietly exchanges the cookie for a new pair on 401. Returns the
 *   new access token (null means the session is dead — trigger logout).
 * - On mount we probe `/auth/admin/refresh` once to pick up a still-valid
 *   session from a previous browser tab.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const refreshInFlight = useRef<Promise<string | null> | null>(null);

  const applyTokens = useCallback((accessToken: string, who: AuthenticatedUser) => {
    setAdminAccessToken(accessToken);
    setUser(who);
  }, []);

  const clear = useCallback(() => {
    setAdminAccessToken(null);
    setUser(null);
  }, []);

  const refresh = useCallback(async (): Promise<string | null> => {
    // Dedupe concurrent 401 storms into a single refresh round trip.
    if (refreshInFlight.current) return refreshInFlight.current;

    const attempt = (async () => {
      try {
        const { data } = await http.post<LoginApiResponse>('/auth/admin/refresh');
        applyTokens(data.accessToken, data.user);
        return data.accessToken;
      } catch {
        clear();
        return null;
      } finally {
        refreshInFlight.current = null;
      }
    })();

    refreshInFlight.current = attempt;
    return attempt;
  }, [applyTokens, clear]);

  const logout = useCallback(async () => {
    try {
      await http.post('/auth/admin/logout');
    } catch {
      /* ignore — the cookie is gone either way */
    }
    clear();
  }, [clear]);

  const login = useCallback(
    async (email: string, password: string): Promise<AuthLoginError | null> => {
      try {
        const { data } = await http.post<LoginApiResponse>('/auth/admin/login', { email, password });
        applyTokens(data.accessToken, data.user);
        return null;
      } catch (err) {
        const status = (err as { response?: { status?: number } }).response?.status;
        if (status === 401)
          return { kind: 'invalid_credentials', message: 'Email or password is incorrect.' };
        if (status === 403)
          return { kind: 'account_disabled', message: 'This account is disabled. Contact a SuperAdmin.' };
        if (status === 429)
          return {
            kind: 'lockout',
            message: 'Too many failed attempts. Try again in a few minutes.',
          };
        return { kind: 'network', message: 'Could not reach the authentication service.' };
      }
    },
    [applyTokens],
  );

  // Expose handlers to the axios interceptor so any 401 on a tab refresh can
  // transparently rehydrate the session.
  useEffect(() => {
    registerAuthHandlers({ refresh, logout: clear });
  }, [refresh, clear]);

  // Try to pick up an existing refresh-cookie session on first mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await refresh();
      if (!cancelled) setIsBootstrapping(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: AuthContextValue = {
    user,
    isAuthenticated: user !== null,
    isBootstrapping,
    login,
    logout,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
